import type {
  ArtworkTint,
  PresetCategory,
  PresetListing,
} from "@pax/api-contract/presets/v1alpha1";
import { Button, focusHaloFlush, panelMotion, press } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import { type CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { ScreenBar, SearchField } from "@/components/ScreenBar";
import { TintCrossfade, type TintCrossfadeHandle } from "@/components/TintCrossfade";
import { largestSource, srcSet } from "@/lib/images";
import { SCREEN_SCROLLER } from "@/shell/Screen";
import { compactCount, shortAgo } from "../facts";
import type { CatalogSort } from "../ordering";
import { firstLandingTint, LandingArtwork } from "./LandingArtwork";
import { WallFilter } from "./WallFilter";

/**
 * The width a small tile aims for. The wall fits as many columns as the room
 * allows rather than a fixed number, so a wider window shows more of the
 * catalog instead of the same twenty covers blown up.
 */
const TILE_WIDTH = 220;

const LARGE = "col-span-2 row-span-2";
const SMALL = "";

/**
 * Where the large tiles fall, for a wall this many columns wide.
 *
 * A cover is a landscape painting, so only a cell twice as wide *and* twice
 * as tall holds it at the shape it was made in — which is why there are two
 * sizes and no others. The wall is laid out in blocks of one large tile and
 * the small ones around it, and the large one moves each block, so the wall
 * has a rhythm rather than a column with a margin beside it.
 *
 * A block is two rows deep where there is room for that. On a narrow wall
 * there is not: at two columns a large tile is the full width, and one every
 * two rows would be most of the wall rather than the thing that breaks it up.
 * So the block gets twice as deep instead of the rhythm being dropped — a
 * phone is the screen that needs it most.
 */
function rhythmFor(columns: number): string[] {
  const rows = columns >= 4 ? 2 : 4;
  const small = (count: number) => Array<string>(count).fill(SMALL);
  const smalls = columns * rows - 4;
  // What the second block puts ahead of its large tile to move it over.
  const ahead = (rows / 2 - 1) * columns + (columns - 2);
  return [LARGE, ...small(smalls), ...small(ahead), LARGE, ...small(smalls - ahead)];
}

/** How far ahead of the foot the next page is asked for. */
const REACH = "900px";

/**
 * A tile's second line, held back until the tile has somewhere to put it. The
 * line height is set so the height it opens to is the height it needs: a line
 * box measured by the font would be clipped by whatever round number the
 * reveal used.
 */
const TILE_DETAIL =
  "block max-h-0 overflow-hidden text-[0.7rem] leading-4 text-white/75 opacity-0 group-hover:max-h-4 group-hover:opacity-100 group-focus-visible:max-h-4 group-focus-visible:opacity-100";

interface SearchWallProps {
  /** The catalog already in the order and at the length it is to be laid out. */
  listings: PresetListing[];
  query: string;
  searchValue: string;
  sort: CatalogSort;
  categories: PresetCategory[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadMoreFailed: boolean;
  onSearchChange: (value: string) => void;
  onSortChange: (sort: CatalogSort) => void;
  onCategoriesChange: (categories: PresetCategory[]) => void;
  onLoadMore: () => void;
}

/**
 * The catalog as one mosaic that keeps going, where prominence is size and
 * nothing else — no rank, no rails, no heading somebody has to write.
 *
 * This is what the store's search link opens onto. Searching narrows this
 * page rather than replacing it, so the results and the catalog are the same
 * object at different lengths, which is the whole reason it is a screen of
 * its own rather than a second layout on the front.
 */
export function SearchWall({
  listings,
  query,
  searchValue,
  sort,
  categories,
  hasNextPage,
  isFetchingNextPage,
  loadMoreFailed,
  onSearchChange,
  onSortChange,
  onCategoriesChange,
  onLoadMore,
}: SearchWallProps) {
  const { wall, ground, point, previewing, syncTint } = usePointedWall();
  const { grid, columns } = useWallColumns();
  const rhythm = rhythmFor(columns);
  const foot = useCatalogFoot({
    enabled: hasNextPage && !isFetchingNextPage && !loadMoreFailed,
    onReach: onLoadMore,
  });

  return (
    <div ref={wall} className="search-wall relative isolate min-h-full bg-screen">
      <TintCrossfade
        ref={ground}
        mode="solid"
        strength={GROUND_STRENGTH}
        className="absolute inset-0 -z-10"
      />
      <ScreenBar>
        <SearchField
          value={searchValue}
          onChange={onSearchChange}
          autoFocus
          maxLength={100}
          placeholder="Search scenarios"
          label="Search scenarios by title or slug"
        />
        <WallFilter
          sort={sort}
          categories={categories}
          onSortChange={onSortChange}
          onCategoriesChange={onCategoriesChange}
        />
      </ScreenBar>

      {listings.length === 0 ? (
        <Nothing
          query={query}
          narrowed={categories.length > 0}
          onClear={() => onSearchChange("")}
        />
      ) : (
        // Held off the window rather than off the screen, so the column
        // standing in the margin does not change the width the wall counts
        // its columns against — a peek would otherwise re-lay the whole
        // mosaic out and back.
        //
        // Room enough under the bar that a tile at the top of the wall can
        // grow without sliding beneath it.
        <div className="app-reading-width pt-6 pb-14">
          <div ref={grid} className="wall">
            <div
              className="wall-grid"
              style={
                {
                  "--wall-columns": columns,
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                } as CSSProperties
              }
            >
              {listings.map((listing, position) => (
                <div key={listing.id} className={`relative ${rhythm[position % rhythm.length]}`}>
                  <WallTile
                    listing={listing}
                    timeline={`--wall-tile-${position}`}
                    previewing={previewing === listing.id}
                    onPoint={point}
                    onTint={tint => syncTint(listing.id, tint)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div ref={foot} className="flex h-10 items-center justify-center">
            {isFetchingNextPage && (
              <p className="text-sm text-muted-foreground">Loading more scenarios…</p>
            )}
            {loadMoreFailed && (
              <Button type="button" variant="outline" size="sm" onClick={onLoadMore}>
                Try loading more
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A tile is a box inside its grid cell rather than the cell itself, so
 * pointing at one grows it out of its own footprint and nothing around it
 * moves. Only the box changes size: the artwork is a crop of it and simply
 * gets more room, and the words stay the size they were.
 *
 * It grows by more than the gap between tiles, so it lifts clear over its
 * neighbours rather than stopping flush against them — a tile that expands to
 * exactly meet the next one reads as a layout that has run out of room. The
 * shadow is what says which of the two is in front.
 *
 * That growth is also the answer to the wall's worst problem: small tiles are
 * too narrow to hold a title, and grown they are not. So the one tile you are
 * reading has the room to be read and the rest stay pictures. Focus does what
 * the pointer does, so a keyboard gets the same wall.
 *
 * Settling at the ends of the scroll is `.wall-tile` in the stylesheet. Each
 * tile reads its own position, so each needs a timeline nothing else on the
 * wall answers to, and it is named here where the position is known. The
 * dimming goes on the tile rather than on its cell because a cell holding an
 * opacity is a stacking context, and a grown tile has to lift over its
 * neighbours rather than inside its own footprint.
 */
function WallTile({
  listing,
  timeline,
  previewing,
  onPoint,
  onTint,
}: {
  listing: PresetListing;
  timeline: string;
  previewing: boolean;
  onPoint: (listing: PresetListing | null) => void;
  onTint: (tint: ArtworkTint) => void;
}) {
  return (
    <Link
      to="/presets/$slug"
      params={{ slug: listing.slug }}
      onMouseEnter={() => onPoint(listing)}
      onMouseLeave={() => onPoint(null)}
      onFocus={() => onPoint(listing)}
      onBlur={() => onPoint(null)}
      style={
        {
          viewTimelineName: timeline,
          animationTimeline: `${timeline}, ${timeline}`,
        } as CSSProperties
      }
      className={`wall-tile group absolute inset-0 overflow-hidden rounded-xl bg-foreground/5 text-left ring-1 ring-foreground/10 hover:-inset-6 hover:z-10 hover:rounded-2xl hover:shadow-2xl focus-visible:-inset-6 focus-visible:z-10 focus-visible:rounded-2xl ${panelMotion} ${press} ${focusHaloFlush}`}
    >
      <img
        src={listing.cover ? largestSource(listing.cover) : undefined}
        srcSet={listing.cover ? srcSet(listing.cover) : undefined}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 size-full object-cover"
      />
      {previewing && (
        <div className="absolute inset-0 animate-in fade-in duration-700">
          <LandingArtwork
            landing={listing.landing}
            fallback={listing.cover}
            onTint={onTint}
            className="size-full"
          />
        </div>
      )}
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2.5 pt-8 pb-2">
        <span className="block truncate text-xs font-medium text-white sm:text-sm">
          {listing.title}
        </span>
        <span className={`${TILE_DETAIL} ${panelMotion}`}>
          {compactCount(listing.gamesStarted)} games &middot; updated{" "}
          {shortAgo(listing.publishedAt)}
        </span>
      </span>
    </Link>
  );
}

function Nothing({
  query,
  narrowed,
  onClear,
}: {
  query: string;
  narrowed: boolean;
  onClear: () => void;
}) {
  return (
    <div className="px-6 py-24 text-center">
      <p className="font-medium">
        {query ? `No scenarios match “${query}”` : "No scenarios of that kind"}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {narrowed ? "Try another kind, or another title." : "Try another title or slug."}
      </p>
      {query && (
        <Button type="button" variant="outline" className="mt-5" onClick={onClear}>
          Clear search
        </Button>
      )}
    </div>
  );
}

export function SearchWallSkeleton() {
  const { grid, columns } = useWallColumns();
  const rhythm = rhythmFor(columns);

  return (
    <div aria-busy className="search-wall min-h-full">
      <ScreenBar>
        <div className="h-9 flex-1 rounded-full bg-foreground/[0.07]" />
        <div className="size-9 shrink-0 rounded-full bg-foreground/[0.07]" />
      </ScreenBar>
      <div className="app-reading-width pt-6 pb-14">
        <div ref={grid} className="wall">
          <div
            className="wall-grid"
            style={
              {
                "--wall-columns": columns,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              } as CSSProperties
            }
          >
            {[...rhythm, ...rhythm].map((cell, position) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: the rhythm is a fixed pattern, and a cell is only its position in it
                key={position}
                className={`animate-pulse rounded-xl bg-foreground/5 ${cell}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** How much of the landing artwork's colour the wall takes while it is being looked at. */
const GROUND_STRENGTH = 70;

/**
 * How long the pointer has to stay on a tile before the wall answers it. The
 * colour is a claim about what is being looked at, and a pointer on its way
 * across the wall is not making one. Letting go does not wait.
 */
const DWELL_MS = 1000;

/** How long after the last scroll event the tiles answer the pointer again. */
const SCROLL_SETTLE_MS = 120;

/** Tiles that have stopped answering the pointer. `.wall-scrolling` in the stylesheet. */
const SCROLLING = "wall-scrolling";

/**
 * The wall on the colour of the landing artwork being looked at.
 *
 * It is the same timed track the scenario page is built on, short of the
 * strength that page opens at. Taking the pointer off puts the strength back
 * to nothing at once.
 *
 * A scroll is the pointer letting go as well. A browser only reconsiders
 * what is under the pointer when the pointer moves, so a tile scrolled out
 * from under a still cursor stays hovered — grown, raised, and holding the
 * wall's colour with nothing under it. For the length of a scroll the tiles
 * stop answering the pointer at all, which drops the hover there and then;
 * moving the pointer again brings them back.
 *
 * Crossing tiles does not enter React state. Only a completed dwell mounts
 * one preview, and later tint frames repaint only the isolated crossfade.
 */
function usePointedWall() {
  const wall = useRef<HTMLDivElement>(null);
  const ground = useRef<TintCrossfadeHandle>(null);
  const dwelling = useRef(0);
  const active = useRef<string | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);

  const paint = useCallback((tint: ArtworkTint | null) => {
    ground.current?.show(tint);
  }, []);

  const point = useCallback(
    (listing: PresetListing | null) => {
      window.clearTimeout(dwelling.current);
      if (listing) {
        dwelling.current = window.setTimeout(() => {
          active.current = listing.id;
          setPreviewing(listing.id);
          paint(firstLandingTint(listing.landing) ?? listing.coverTint);
        }, DWELL_MS);
      } else {
        active.current = null;
        setPreviewing(null);
        paint(null);
      }
    },
    [paint],
  );

  const syncTint = useCallback(
    (presetId: string, tint: ArtworkTint) => {
      if (active.current === presetId) paint(tint);
    },
    [paint],
  );

  useEffect(() => {
    const element = wall.current;
    const scroller = element?.closest<HTMLElement>(SCREEN_SCROLLER);
    if (!element || !scroller) return;
    let settling = 0;

    const scrolled = () => {
      window.clearTimeout(settling);
      if (!element.classList.contains(SCROLLING)) {
        element.classList.add(SCROLLING);
        point(null);
      }
      settling = window.setTimeout(() => element.classList.remove(SCROLLING), SCROLL_SETTLE_MS);
    };

    scroller.addEventListener("scroll", scrolled, { passive: true });

    return () => {
      window.clearTimeout(settling);
      scroller.removeEventListener("scroll", scrolled);
    };
  }, [point]);

  useEffect(() => () => window.clearTimeout(dwelling.current), []);

  return { wall, ground, point, previewing, syncTint };
}

/**
 * How many columns the wall has room for.
 *
 * The count is measured rather than declared at a handful of breakpoints,
 * because the tile is the fixed thing here and the window is not: a wall that
 * keeps four columns on a wide monitor answers the extra room by enlarging
 * covers, when what the room is for is more of the catalog.
 *
 * Everything else the grid needs — the row height that keeps both sizes at
 * the covers' own ratio — is derived from this in CSS, off the wall's own
 * inline size.
 *
 * The count is held by the wall rather than by the grid, because the grid
 * comes and goes: a search that matches nothing takes it off the screen and
 * clearing that search puts a new one back. So the measuring is hung off the
 * ref itself, which is handed whichever grid is currently up, and the last
 * count stands while there is none — a wall laid out for the window it is in
 * is the right thing to come back to.
 */
function useWallColumns() {
  const [columns, setColumns] = useState(4);

  const grid = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;

    const measure = () => setColumns(Math.max(2, Math.round(element.clientWidth / TILE_WIDTH)));

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { grid, columns };
}

/**
 * The wall keeps going. The next page is asked for when the foot of the one
 * you have comes within a screen of you, rather than when a button is
 * pressed — a mosaic that ends in a **Load more** is a list again.
 */
function useCatalogFoot({ enabled, onReach }: { enabled: boolean; onReach: () => void }) {
  const foot = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = foot.current;
    if (!element || !enabled) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onReach();
      },
      { root: document.querySelector(SCREEN_SCROLLER), rootMargin: REACH },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [enabled, onReach]);

  return foot;
}
